import { styled } from "@mui/material/styles";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import { CheckedIcon, FilterExpandArrow, UnCheckedIcon } from "../../svgIcons";
import { isMobile } from "react-device-detect";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import { Checkbox, debounce, FormControlLabel } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { GetAllAccessoriesWithFilters } from "../../api/services/products.api";
import ProductsGrid from "../ProductsGrid/ProductsGrid";
import Box from "@mui/material/Box";

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  // border: `1px solid ${theme.palette.divider}`,
  borderRadius: "12px",
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&::before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={<FilterExpandArrow style={{}} />}
    {...props}
  />
))(({ theme }) => ({
  // backgroundColor: "rgba(0, 0, 0, .03)",
  flexDirection: "row-reverse",
  paddingLeft: "7px",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1),
    "& p": {
      fontSize: isMobile ? "12px" : "14px",
      fontWeight: 600,
      lineHeight: "20px",
      opacity: 0.8,
      color: "#000000",
      marginLeft: "15px",
    },
  },
  ...theme.applyStyles("dark", {
    backgroundColor: "rgba(255, 255, 255, .05)",
  }),
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  paddingTop: "0px",
  paddingLeft: "0px",
  // borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

const StyledLabel = styled(FormControlLabel)({
  fontSize: "0.875rem",
  fontWeight: "500",
  color: "#555", // Elegant color for label
  margin: "0.25rem 0",
  "& .MuiCheckbox-root": {
    padding: "4px", // Smaller padding for compact look
    "&.Mui-checked": {
      color: "#2793ff", // Elegant color for checked state
    },
  },
  "& .MuiTypography-root": {
    fontSize: isMobile ? "12px" : "0.875rem",
    // fontWeight: "bold",
    marginLeft: "10px",
  },
  "&:hover": {
    // backgroundColor: "rgba(0, 119, 255, 0.06);",
    borderRadius: "10px",
  },
});

const AccessoryTypeEnum = [
  "ScreenProtector",
  "MobilePhoneCase",
  "WirelessCharger",
  "PowerBank",
  "Cable",
  "ChargingAdapter",
  "CarAccessory",
  "LaptopAccessory",
  "TabletAccessory",
];

const AccessoriesPage = ({ isMobileVersion }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [params, setParams] = useState({
    limit: limit,
    minPrice: 0,
    maxPrice: 8000,
  });

  useEffect(() => {
    setLoading(true);
    GetAllAccessoriesWithFilters({ ...params, pageNumber: 1 })
      .then((resp) => {
        setPage(1);
        if (resp.data) {
          setTotal(resp.data.total);
          setData(resp.data.data);
        }
      })
      .catch(() => {
        enqueueSnackbar(t("errorPleaseContactWebsiteAdministrator"), {
          variant: "error",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [params]);

  const seeMoreCallBack = () => {
    setLoading(true);
    GetAllAccessoriesWithFilters({ ...params, pageNumber: page + 1 })
      .then((resp) => {
        setPage(page + 1);
        if (resp.data) {
          setData([...data, ...resp.data.data]);
        }
      })
      .catch(() => {
        enqueueSnackbar(t("errorPleaseContactWebsiteAdministrator"), {
          variant: "error",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const changePriceCallBack = useMemo(
    () =>
      debounce((value) => {
        if (value) {
          setParams({ ...params, minPrice: value[0], maxPrice: value[1] });
        }
      }, 500),
    [],
  );

  const onChangeAccessoryTypeEnum = (AccessoryType) => {
    if (params["accessoryType"] === AccessoryType) {
      setParams({
        ...params,
        accessoryType: null,
      });
    } else {
      setParams({
        ...params,
        accessoryType: AccessoryType,
      });
    }
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
      }}
    >
      <ProductsGrid
        title={t("accessories")}
        minAmount={0}
        maxAmount={8000}
        data={data}
        loading={loading}
        total={total}
        seeMoreCallBack={seeMoreCallBack}
        changePriceCallBack={changePriceCallBack}
        isMobileVersion={isMobileVersion}
      >
        <div>
          <StyledLabel
            control={
              <Checkbox
                onClick={(event) =>
                  setParams({ ...params, isSecondHand: event.target.checked })
                }
                icon={<UnCheckedIcon />}
                checkedIcon={<CheckedIcon />}
                size="small"
              />
            }
            label={t("findAsSecondHand")}
            sx={{
              width: "100%",
              background: "#f7faff",
              borderRadius: "10px",
              padding: "5px",
              "& span": {
                fontWeight: 600,
                fontSize: "20px",
              },
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "5px",
          }}
        >
          <Box sx={{ display: "flex", gap: "5px", flexDirection: "column" }}>
            <Box display="flex" flexDirection="column">
              {AccessoryTypeEnum.map((value, index) => (
                <StyledLabel
                  key={value}
                  control={
                    <Checkbox
                      onClick={() => onChangeAccessoryTypeEnum(index)}
                      icon={<UnCheckedIcon />}
                      checkedIcon={<CheckedIcon />}
                      size="small"
                      onChange={(event) => {}}
                    />
                  }
                  label={t(`AccessoryTypeEnum${value}`)}
                />
              ))}
            </Box>
          </Box>
        </div>
      </ProductsGrid>
    </div>
  );
};

export default AccessoriesPage;
